/**
 * WebAuthn/Passkey Configuration
 *
 * Relying Party (RP) settings for WebAuthn ceremonies.
 * The RP ID must match the domain where passkeys are used.
 *
 * IMPORTANT: WebAuthn requires a "secure context" which means:
 * - HTTPS on any domain, OR
 * - HTTP on localhost/127.0.0.1 only
 *
 * For local development, use http://localhost:3002
 */

/**
 * Determine the RP ID based on the current hostname
 */
export function getRpIdFromHostname(hostname: string): string {
    // Production
    if (hostname.includes("outfame.com")) {
        return "outfame.com"
    }
    // Local development on localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "localhost"
    }
    // Fallback
    return hostname
}

export const getPasskeyConfig = () => {
    const isProd = process.env.NODE_ENV === "production"

    // For server-side, we need to support multiple origins
    // The actual RP ID is determined at runtime based on the request origin
    return {
        // RP ID - for server-side verification, we'll determine dynamically
        // For client-side, use getRpIdFromHostname()
        rpID: isProd ? "outfame.com" : "localhost",

        // Human-readable name shown in authenticator prompts
        rpName: "Outfame",

        // Expected origins for verification (with protocol and port)
        expectedOrigins: isProd ? ["https://outfame.com", "https://www.outfame.com"] : ["http://localhost:3002", "http://localhost:3003"],

        // Timeout for WebAuthn ceremonies (60 seconds)
        timeout: 60000,

        // Attestation conveyance preference
        // 'none' is recommended for privacy - we don't need to verify authenticator manufacturer
        attestationType: "none" as const,

        // Authenticator selection preferences
        authenticatorSelection: {
            // Prefer platform authenticators (Touch ID, Face ID, Windows Hello)
            authenticatorAttachment: "platform" as const,
            // Prefer resident keys (discoverable credentials) for passwordless
            residentKey: "preferred" as const,
            // Require user verification (biometric or PIN)
            userVerification: "preferred" as const
        },

        // Supported public key algorithms (in order of preference)
        // -7 = ES256 (ECDSA with P-256 and SHA-256) - most common
        // -257 = RS256 (RSASSA-PKCS1-v1_5 with SHA-256) - fallback
        supportedAlgorithmIDs: [-7, -257]
    }
}

// Type for passkey credential stored in passkeys column
export interface StoredPasskeyCredential {
    id: string // Base64URL credential ID
    publicKey: string // Base64URL public key
    counter: number // Signature counter for replay protection
    transports?: AuthenticatorTransport[]
    deviceName: string // User-friendly device name
    createdAt: string // ISO timestamp
    lastUsedAt: string // ISO timestamp
}

// Type for passkey data in passkeys column
export interface PasskeyData {
    credentials: StoredPasskeyCredential[]
    promptDismissCount: number
    promptLastDismissedAt?: string
}

// Legacy type - no longer used since passkeys moved to dedicated column
export interface UserInfoWithPasskeys {
    passkeys?: PasskeyData
    // ... other existing fields like google_ads_ctx, timeZone, etc.
    [key: string]: unknown
}

// Biometric type for device-specific icons
export type BiometricType = "face-id" | "touch-id" | "windows-hello" | "passkey"

/**
 * Check if the current context is secure (required for WebAuthn)
 * Secure context = HTTPS or localhost
 */
export function isSecureContext(): boolean {
    if (typeof window === "undefined") return false

    // Use the native isSecureContext if available
    if (typeof window.isSecureContext === "boolean") {
        return window.isSecureContext
    }

    // Fallback check
    const protocol = window.location.protocol
    const hostname = window.location.hostname

    // HTTPS is always secure
    if (protocol === "https:") return true

    // localhost variations are secure even over HTTP
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
        return true
    }

    // .localhost domains are secure
    if (hostname.endsWith(".localhost")) return true

    return false
}

/**
 * Detect the most likely biometric type based on user agent
 * Used to show appropriate icons (Face ID, Touch ID, etc.)
 */
export function getBiometricType(): BiometricType {
    if (typeof window === "undefined") return "passkey"

    const ua = navigator.userAgent

    // Try modern userAgentData API first (Chrome 90+, Edge 90+)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uaData = (navigator as any).userAgentData
    if (uaData?.platform) {
        const platform = uaData.platform.toLowerCase()
        if (platform === "macos") return "touch-id"
        if (platform === "windows") return "windows-hello"
        if (platform === "ios") return "face-id"
    }

    // Fallback to user agent string parsing
    // iPhone - most modern iPhones have Face ID (iPhone X and later)
    if (/iPhone/.test(ua)) {
        return "face-id"
    }

    // iPad - check for Face ID models (iPad Pro 2018+)
    if (/iPad/.test(ua)) {
        // iPad Pro with Face ID has specific model identifiers, but we can't reliably detect
        // Default to Touch ID as most iPads still have it
        return "touch-id"
    }

    // macOS - MacBooks with Touch ID (2016 MacBook Pro and later)
    // Check both userAgent and platform for macOS
    if (/Macintosh|Mac OS X|MacIntel/.test(ua) || /Mac/.test(navigator.platform || "")) {
        return "touch-id"
    }

    // Windows 10/11 - Windows Hello
    if (/Windows NT 1[01]/.test(ua) || /Windows/.test(ua)) {
        return "windows-hello"
    }

    // Android - most use fingerprint
    if (/Android/.test(ua)) {
        return "passkey" // Generic icon for Android as it varies
    }

    // Default fallback
    return "passkey"
}

/**
 * Get human-readable name for the biometric type
 */
export function getBiometricDisplayName(type: BiometricType): string {
    const names: Record<BiometricType, string> = {
        "face-id": "Face ID",
        "touch-id": "Touch ID",
        "windows-hello": "Windows Hello",
        passkey: "Passkey"
    }
    return names[type]
}

/**
 * Check if WebAuthn is supported in the current browser
 * Returns detailed info for debugging
 */
export interface PasskeySupportResult {
    supported: boolean
    reason?: string
    isSecureContext: boolean
    hasWebAuthnApi: boolean
    hasPlatformAuthenticator: boolean
}

export async function checkPasskeySupport(): Promise<PasskeySupportResult> {
    if (typeof window === "undefined") {
        return {
            supported: false,
            reason: "Server-side rendering",
            isSecureContext: false,
            hasWebAuthnApi: false,
            hasPlatformAuthenticator: false
        }
    }

    const secure = isSecureContext()
    const hasApi = !!window.PublicKeyCredential

    if (!secure) {
        console.warn("[Passkeys] Not in a secure context. WebAuthn requires HTTPS or localhost.\n" + `Current origin: ${window.location.origin}`)
        return {
            supported: false,
            reason: "Not a secure context. Use HTTPS or localhost.",
            isSecureContext: false,
            hasWebAuthnApi: hasApi,
            hasPlatformAuthenticator: false
        }
    }

    if (!hasApi) {
        return {
            supported: false,
            reason: "WebAuthn API not available in this browser",
            isSecureContext: secure,
            hasWebAuthnApi: false,
            hasPlatformAuthenticator: false
        }
    }

    // Check for platform authenticator (Face ID, Touch ID, Windows Hello)
    try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        if (!available) {
            return {
                supported: false,
                reason: "No platform authenticator available (Touch ID, Face ID, Windows Hello)",
                isSecureContext: secure,
                hasWebAuthnApi: true,
                hasPlatformAuthenticator: false
            }
        }
        return {
            supported: true,
            isSecureContext: secure,
            hasWebAuthnApi: true,
            hasPlatformAuthenticator: true
        }
    } catch (error) {
        console.error("[Passkeys] Error checking platform authenticator:", error)
        return {
            supported: false,
            reason: "Error checking platform authenticator availability",
            isSecureContext: secure,
            hasWebAuthnApi: true,
            hasPlatformAuthenticator: false
        }
    }
}

/**
 * Simple boolean check for passkey support
 */
export async function isPasskeySupported(): Promise<boolean> {
    const result = await checkPasskeySupport()
    return result.supported
}

/**
 * Synchronous quick check for passkey support (optimistic)
 * Returns true if secure context + WebAuthn API exists
 * Used for immediate UI rendering before async check completes
 */
export function canProbablyUsePasskeys(): boolean {
    if (typeof window === "undefined") return false
    return isSecureContext() && !!window.PublicKeyCredential
}
